import { useMemo, useCallback } from 'react';
import { useWorkspace } from '../Context/WorkspaceContext';

/**
 * Custom hook for workspace permissions
 * Provides easy-to-use permission checking functions based on WorkspaceContext
 */
export const useWorkspacePermissions = () => {
  const { getCurrentRole, isWorkspaceAdmin, loading: isLoading } = useWorkspace();
  
  const userRole = getCurrentRole();
  const isAdmin = isWorkspaceAdmin();
  

  // Memoize permission checks for performance
  const permissions = useMemo(() => ({
    // Basic role checks
    isAdmin: isAdmin,
    isUser: userRole === 'user',

    // Specific permission checks - All users can access features
    canAccessAISettings: true,
    canModifyAISettings: true,
    canCreateChatbots: true,
    canDeleteChatbots: true,
    canManageIntegrations: true,
    canViewAnalytics: true,

    // Workspace/User management - Admin only
    canManageWorkspace: isAdmin,
    canInviteUsers: isAdmin,
    canManageUsers: isAdmin,
    canManageInvitations: isAdmin, // Admins can accept/decline/cancel pending invitations
    canUpdateWorkspaceState: isAdmin, // Only admins can update workspace state
    // Note: canDeleteWorkspace is handled separately - only workspace creator can delete

    // Brand settings - All users can access
    canAccessBrandSettings: true,
    canModifyBrandSettings: true,

    // AI Model specific permissions - All users can access
    canChangeAIModel: true,
    canAdjustTemperature: true,
    canResetAISettings: true,

    // Training permissions - All users can access
    canStartTraining: true,
    canUploadDocuments: true,
    canDeleteDocuments: true,

    // Chat permissions - All users can access
    canViewChats: true,
    canDeleteChats: true,
    canExportChats: true,

    // Lead permissions - All users can access
    canViewLeads: true,
    canExportLeads: true,
    canDeleteLeads: true,

    // General data permissions - All users can access
    canExportData: true,
    canImportData: true,
  }), [userRole, isAdmin]);

  const checkPermission = useCallback((permission) => {
    if (typeof permission === 'function') {
      return permission(userRole);
    }
    return permissions[permission] || false;
  }, [permissions, userRole]);

  return {
    ...permissions,
    userRole,
    isLoading,

    // Helper functions
    hasRole: (role) => userRole === role,
    hasAnyRole: (roles) => roles.includes(userRole),

    // Permission checking function for custom permissions
    checkPermission
  };
};

/**
 * Hook for checking specific workspace permission
 * @param {string|function} permission - Permission to check
 * @returns {boolean} Whether user has the permission
 */
export const usePermission = (permission) => {
  const { checkPermission } = useWorkspacePermissions();
  return checkPermission(permission);
};

/**
 * Hook for admin-only access
 * @returns {object} Admin access status and loading state
 */
export const useAdminAccess = () => {
  const { isAdmin, isLoading } = useWorkspacePermissions();

  return {
    hasAccess: isAdmin,
    isLoading,
    deniedReason: isAdmin ? null : 'Administrator access required'
  };
};

/**
 * Hook for feature-specific permissions
 * Useful for enabling/disabling specific features based on workspace role
 */
export const useFeaturePermissions = () => {
  const permissions = useWorkspacePermissions();

  return {
    // AI Settings feature permissions
    aiSettings: {
      canView: true, // Users can view but not modify
      canModify: permissions.canModifyAISettings,
      canChangeModel: permissions.canChangeAIModel,
      canAdjustTemperature: permissions.canAdjustTemperature,
      canReset: permissions.canResetAISettings,
    },

    // Chatbot management permissions
    chatbotManagement: {
      canCreate: permissions.canCreateChatbots,
      canDelete: permissions.canDeleteChatbots,
      canModifySettings: permissions.canModifyAISettings,
    },

    // Training permissions
    training: {
      canStart: permissions.canStartTraining,
      canUpload: permissions.canUploadDocuments,
      canDelete: permissions.canDeleteDocuments,
    },

    // Data permissions
    data: {
      canView: true,
      canExport: permissions.canExportData,
      canImport: permissions.canImportData,
      canDelete: true,
    },

    // Chatbot personality permissions
    chatbotPersonality: {
      canView: true, // Users can view but not modify
      canModify: permissions.canModifyAISettings,
      canCreateTemplates: permissions.canModifyAISettings,
      canDeleteTemplates: permissions.canModifyAISettings,
      canEditPrompts: permissions.canModifyAISettings,
    },

    // Chatbot customization permissions
    chatbotCustomization: {
      canView: true, // Users can view but not modify
      canModify: permissions.canModifyAISettings,
      canChangeTheme: permissions.canModifyAISettings,
      canUploadFiles: permissions.canModifyAISettings,
      canModifyColors: permissions.canModifyAISettings,
      canAdjustSettings: permissions.canModifyAISettings,
      canSaveTemplates: permissions.canModifyAISettings,
      canResetToDefault: permissions.canModifyAISettings,
    },

    // Integration permissions
    integration: {
      canView: true, // Users can view but not modify
      canCopyCode: true, // Users can copy integration codes
      canConnect: permissions.canModifyAISettings, // Admin-only for connecting/disconnecting integrations
      canDisconnect: permissions.canModifyAISettings,
      canManageWebhooks: permissions.canModifyAISettings,
      canModifySettings: permissions.canModifyAISettings,
    },

    // Agent Settings permissions
    agentSettings: {
      canView: true, // Users can view but not modify
      canModify: permissions.canModifyAISettings,
      canChangeName: permissions.canModifyAISettings,
      canModifyUrls: permissions.canModifyAISettings,
      canChangeStatus: permissions.canModifyAISettings,
      canModifyRateLimit: permissions.canModifyAISettings,
      canDeleteAgent: permissions.canDeleteChatbots,
    },

    // Workspace permissions
    workspace: {
      canManage: permissions.canManageWorkspace,
      canInvite: permissions.canInviteUsers,
      canRemoveUsers: permissions.canManageUsers,
      canManageInvitations: permissions.canManageInvitations,
      canUpdateState: permissions.canUpdateWorkspaceState,
      // Note: canDelete is handled separately - only workspace creator can delete
    }
  };
};

export default useWorkspacePermissions;